// Lightweight animated trading background: SVG candlesticks + EMA/RSI lines.
// Uses SMIL <animateTransform> (single GPU transform, no JS loop, no images).
const W = 1200;
const H = 460;

function buildSeries() {
  const N = 46;
  const step = W / N;
  let seed = 73;
  const rnd = () => { seed = (seed * 1103515245 + 12345) & 0x7fffffff; return seed / 0x7fffffff; };
  const top = 60, bot = H - 120;
  const y = (v) => bot - v * (bot - top); // v in [0,1] -> pixel (higher v = higher)
  const closes = [];
  let p = 0.42;
  for (let i = 0; i < N; i++) {
    p += (rnd() - 0.46) * 0.11;
    p = Math.max(0.12, Math.min(0.9, p));
    closes.push(p);
  }
  const candles = [];
  const volBase = H - 16;      // baseline volume bars
  const volMax = 92;           // tinggi maksimum bar volume
  for (let i = 0; i < N; i++) {
    const cx = i * step + step / 2;
    const open = i === 0 ? closes[0] : closes[i - 1];
    const close = closes[i];
    const up = close >= open;
    const hi = Math.min(0.96, Math.max(open, close) + rnd() * 0.05 + 0.01);
    const lo = Math.max(0.06, Math.min(open, close) - rnd() * 0.05 - 0.01);
    const bodyTop = y(Math.max(open, close));
    const bodyBot = y(Math.min(open, close));
    const vol = 0.25 + Math.abs(close - open) * 3.2 + rnd() * 0.55; // pseudo-volume
    const volH = Math.max(4, Math.min(volMax, vol * volMax));
    candles.push({ cx, wickTop: y(hi), wickBot: y(lo), bodyTop, bodyH: Math.max(2, bodyBot - bodyTop), up, volH });
  }
  // EMA(9) of closes
  const ema = [];
  const k = 2 / (9 + 1);
  closes.forEach((c, i) => { ema.push(i === 0 ? c : c * k + ema[i - 1] * (1 - k)); });
  const emaPath = ema.map((v, i) => `${i === 0 ? "M" : "L"} ${(i * step + step / 2).toFixed(1)} ${y(v).toFixed(1)}`).join(" ");
  // RSI-like oscillator band near bottom
  const rTop = H - 116, rBot = H - 86;
  const ry = (v) => rBot - v * (rBot - rTop);
  const rsiPath = closes.map((c, i) => {
    const osc = 0.5 + Math.sin(i * 0.5) * 0.32 + (c - 0.5) * 0.3;
    return `${i === 0 ? "M" : "L"} ${(i * step + step / 2).toFixed(1)} ${ry(Math.max(0.05, Math.min(0.95, osc))).toFixed(1)}`;
  }).join(" ");
  const lastEmaY = y(ema[ema.length - 1]);
  return { candles, emaPath, rsiPath, lastEmaY };
}

const S = buildSeries();

const volBaseline = H - 16;

const Series = () => (
  <>
    {S.candles.map((c, i) => (
      <rect key={`v${i}`} x={c.cx - 6} y={volBaseline - c.volH} width="12" height={c.volH} rx="1" fill={c.up ? "#10B981" : "#F43F5E"} opacity="0.28" />
    ))}
    {S.candles.map((c, i) => (
      <g key={i}>
        <line x1={c.cx} x2={c.cx} y1={c.wickTop} y2={c.wickBot} stroke={c.up ? "#10B981" : "#F43F5E"} strokeWidth="1.5" />
        <rect x={c.cx - 6} y={c.bodyTop} width="12" height={c.bodyH} rx="1.5" fill={c.up ? "#10B981" : "#F43F5E"} />
      </g>
    ))}
    <path d={S.rsiPath} fill="none" stroke="#EAB308" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.7" />
    <path d={S.emaPath} fill="none" stroke="#34D399" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx={W - 6} cy={S.lastEmaY} r="4" fill="#34D399">
      <animate attributeName="opacity" values="1;0.2;1" dur="1.6s" repeatCount="indefinite" />
    </circle>
  </>
);

export default function HeroChartBg() {
  return (
    <div
      aria-hidden="true"
      className="absolute inset-0 pointer-events-none select-none"
      style={{
        opacity: 0.4,
        WebkitMaskImage: "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.35) 30%, #000 62%, #000 100%)",
        maskImage: "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.35) 30%, #000 62%, #000 100%)",
      }}
      data-testid="hero-chart-bg"
    >
      <svg width="100%" height="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid slice">
        <g>
          <animateTransform attributeName="transform" type="translate" from="0 0" to={`-${W} 0`} dur="44s" repeatCount="indefinite" />
          <g transform="translate(0,0)"><Series /></g>
          <g transform={`translate(${W},0)`}><Series /></g>
        </g>
      </svg>
    </div>
  );
}
